// Join League - Accept league invitation
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLeague } from '../contexts/LeagueContext';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card } from '../components';

const JoinLeague = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getInvitation, acceptInvite, leagues } = useLeague();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true);
        const invite = await getInvitation(inviteId);
        if (!invite) {
          setError('Invitation not found');
        } else if (invite.status !== 'active') {
          setError('This invitation has already been used');
        } else if (new Date(invite.expiresAt) < new Date()) {
          setError('This invitation has expired');
        } else {
          setInvitation(invite);
        }
      } catch (err) {
        console.error('Error loading invitation:', err);
        setError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [inviteId, getInvitation]);

  // Check if user is already a member
  const alreadyMember = invitation && leagues.some(l => l.id === invitation.leagueId);

  const handleJoin = async () => {
    if (!user) {
      // Store invite ID and redirect to login
      localStorage.setItem('pendingLeagueInvite', inviteId);
      navigate('/');
      return;
    }

    setJoining(true);
    setError('');

    try {
      const leagueId = await acceptInvite(inviteId);
      navigate(`/league/${leagueId}`);
    } catch (err) {
      console.error('Error joining league:', err);
      setError('Failed to join league: ' + err.message);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-bold mb-4">Invitation Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  if (alreadyMember) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-xl font-bold mb-4">Already a Member</h2>
          <p className="text-gray-600 mb-6">
            You're already a member of <strong>{invitation.leagueName}</strong>!
          </p>
          <Button onClick={() => navigate(`/league/${invitation.leagueId}`)}>
            Go to League
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center">
        <div className="text-6xl mb-4">🏆</div>
        <h2 className="text-xl font-bold mb-2">You're Invited!</h2>
        <p className="text-gray-600 mb-2">
          <strong>{invitation.invitedByName}</strong> has invited you to join
        </p>
        <h3 className="text-2xl font-bold text-blue-600 mb-6">{invitation.leagueName}</h3>

        {!user ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Sign in to accept this invitation</p>
            <Button onClick={() => {
              localStorage.setItem('pendingLeagueInvite', inviteId);
              navigate('/');
            }} fullWidth>
              Sign In to Join
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              You'll be joining as <strong>{user.displayName}</strong>
            </p>
            <Button onClick={handleJoin} disabled={joining} fullWidth>
              {joining ? 'Joining...' : 'Join League'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              Cancel
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default JoinLeague;
